import { memo, useCallback, type ReactElement, type ChangeEvent } from 'react';

import type { StandardText } from '@buying/library';
import type { TextFieldProps } from '@mui/material/TextField';

import {
    ClearableMultilineTextField,
    scrollToBottomOfTextField,
    useDefaultInputSize,
} from '@buying/library';

import { StandardTextSelector } from './StandardTextSelector';
import {
    generateStandardTextFieldHelperText,
    getStandardTextsFromNumbers,
    joinStandardTexts,
} from './utils';

interface StandardTextFieldValue {
    text: string;
    selectedTextNumbers: Array<StandardText['standardTextNumber']>;
}

/**
 * @param options - Array of StandardText options to choose from
 * @param selectedNumbers - Array of selected standard text numbers
 * @param value - The current text value
 * @param onChange - Callback when text value changes
 * @param textFieldProps - Props passed to the underlying TextField/OutlinedInput
 * @param fieldLabel - Label for the field
 * @param nothingSelectedLabel - Label shown when nothing is selected in the selector
 * @param isLocked - If true, the field should be read only and the selector replaced with a lock icon @default false
 */
interface StandardTextFieldProps
    extends Omit<
        TextFieldProps,
        'id' | 'multiline' | 'onChange' | 'size' | 'value'
    > {
    fieldLabel: string;
    nothingSelectedLabel: string;
    options: Array<StandardText>;
    value: StandardTextFieldValue | undefined;
    onChange?: (value: StandardTextFieldValue) => void;
    isLocked?: boolean;
}

const StandardTextField = memo(
    ({
        fieldLabel,
        nothingSelectedLabel,
        options,
        value,
        error,
        onChange,
        isLocked = false,
        ...textFieldProps
    }: StandardTextFieldProps): ReactElement => {
        const inputSize = useDefaultInputSize();

        const isReadOnly =
            isLocked ||
            (
                textFieldProps.slotProps?.input as
                    | { readOnly?: boolean }
                    | undefined
            )?.readOnly;
        const isDisabled = textFieldProps.disabled;

        const fieldId = `${fieldLabel.split(/\s+/).join('-').toLowerCase()}-field`;

        const handleClearField = useCallback((): void => {
            if (onChange) {
                onChange({ text: '', selectedTextNumbers: [] });
            }
        }, [onChange]);

        const handleSelectingStandardTexts = useCallback(
            (option: StandardText): void => {
                let newSelectedNumbers: Array<
                    StandardText['standardTextNumber']
                >;

                if (
                    value?.selectedTextNumbers.includes(
                        option.standardTextNumber
                    )
                ) {
                    newSelectedNumbers = value.selectedTextNumbers.filter(
                        (number) => number !== option.standardTextNumber
                    );
                } else {
                    newSelectedNumbers = [
                        ...(value?.selectedTextNumbers ?? []),
                        option.standardTextNumber,
                    ];
                }

                const selectedStandardTexts = getStandardTextsFromNumbers(
                    options,
                    newSelectedNumbers
                );

                const newText = selectedStandardTexts
                    ? joinStandardTexts(selectedStandardTexts)
                    : '';

                onChange?.({
                    text: newText,
                    selectedTextNumbers: newSelectedNumbers,
                });

                scrollToBottomOfTextField(fieldId);
            },
            [fieldId, onChange, options, value?.selectedTextNumbers]
        );

        const handleChange = useCallback(
            (event: ChangeEvent<HTMLInputElement>): void => {
                onChange?.({
                    text: event.target.value,
                    selectedTextNumbers: [], // Clear selected texts when user types manually
                });
            },
            [onChange]
        );

        return (
            <ClearableMultilineTextField
                {...textFieldProps}
                id={fieldId}
                fieldLabel={fieldLabel}
                disabled={isDisabled}
                isReadOnly={isReadOnly}
                labelRightContent={
                    <StandardTextSelector
                        options={options}
                        selected={
                            getStandardTextsFromNumbers(
                                options,
                                value?.selectedTextNumbers ?? []
                            ) ?? []
                        }
                        nothingSelectedLabel={nothingSelectedLabel}
                        isDisabled={isDisabled ?? isReadOnly}
                        isLocked={isLocked}
                        onSelect={handleSelectingStandardTexts}
                    />
                }
                helperText={generateStandardTextFieldHelperText(
                    value?.text ?? ''
                )}
                value={value?.text ?? ''}
                size={inputSize}
                error={error}
                onChange={handleChange}
                onClear={handleClearField}
            />
        );
    }
);

StandardTextField.displayName = 'StandardTextField';

export { StandardTextField };
export type { StandardTextFieldProps };
